// lib
import React, { useCallback, useRef } from 'react'
import { useParams } from 'react-router'

// api
import {
  ProjectMilestoneSortingKeys,
  ProjectType,
  SortDirection,
  useMilestones
} from '@lopo-tech/lopo-api'

// components
import LoadingSpinner from 'src/components/loading_spinner/loading_spinner'
import MetaTags from 'src/components/meta_tags/meta_tags'
import { ProjectNotFound } from 'src/components/projects/not_found/project_not_found'

// containers/layout
import HeaderProject from 'src/components/header/subheaders/pages/project_detail/header_project'
import {
  DETAILS_PAGE_TITLE,
  INFRASTRUCTURE_PROJECTSFEED_DESCRIPTION,
  INFRASTRUCTURE_PROJECTSFEED_TITLE,
  PROJECTSFEED_DESCRIPTION,
  PROJECTSFEED_TITLE
} from 'src/constants/meta_tags'
import { getProjectRoutes } from 'src/constants/urls'
import ListWrapper from 'src/containers/smart_components/list_wrapper/list_wrapper'
import StandardPageTitle from 'src/containers/smart_components/page_title/page_title'
import LayoutNoMap from 'src/layout/layout_no_map'
import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'

// styles
import { ProjectMilestones } from 'src/components/projects/milestones/project_milestones'
import { textResources } from 'src/lang/de'

const t = textResources.projects

const PER_PAGE = 10

interface IProps {
  projectType: ProjectType;
}

export const ProjectMilestonesPage: React.FC<IProps> = ({ projectType }) => {
  const routes = getProjectRoutes(projectType)
  const { slug } = useParams<{ slug: string }>()
  const locationShape = useGetAppLocationShape()
  const isInfrastructureProject = projectType === ProjectType.InfrastructureProject

  const { data, loading, error, fetchMore } = useMilestones({
    variables: {
      slug,
      page: 1,
      perPage: PER_PAGE,
      sorting: {
        fieldName: ProjectMilestoneSortingKeys.HappenedOn,
        direction: SortDirection.Desc
      }
    }
  })

  const projectName = data?.project?.name || t.details.nameFallback

  // we need to ensure that the callback always has the latest loading state
  // to avoid multiple fetchMore calls being triggered due to the getNextPage
  // being in a throttled fashion
  const loadingRef = useRef(loading)
  loadingRef.current = loading

  const pageInfo = data?.project?.milestones?.pageInfo
  const getNextPage = useCallback(() => {
    if (pageInfo?.nextPage && !loadingRef.current) {
      fetchMore({ variables: { page: pageInfo.nextPage } })
    }
  }, [fetchMore, pageInfo?.nextPage])

  const isInitialLoading = loading && !data
  if (isInitialLoading) {
    return <LoadingSpinner shown={true} />
  }

  const project = data?.project
  const cannotShow = project && !project.canShowMilestones.value
  const isNotFound = Boolean(error) || !project || cannotShow

  const titleResolver = isInfrastructureProject ? INFRASTRUCTURE_PROJECTSFEED_TITLE : PROJECTSFEED_TITLE
  const descriptionResolver = isInfrastructureProject ? INFRASTRUCTURE_PROJECTSFEED_DESCRIPTION : PROJECTSFEED_DESCRIPTION
  const feedTitle = locationShape ? titleResolver(locationShape.name) : undefined
  const description = locationShape ? descriptionResolver(locationShape.name) : undefined
  const title = isNotFound ? feedTitle : DETAILS_PAGE_TITLE(projectName)

  return (
    <>
      <MetaTags
        description={description}
        geo_placename={locationShape?.name}
        geo_position={
          locationShape &&
          `${locationShape.center.latitude};${locationShape.center.longitude}`
        }
        og_url={document.location.toString()}
        title={title}
      />
      <LayoutNoMap
        subHeader={
          <HeaderProject
            title={projectName}
            fallbackRoute={routes.detail(slug)}
            showMoreButton={false}
          />
        }
        pageTitle={
          <StandardPageTitle
            text={isInfrastructureProject ? t.common.infrastructureProjects : t.common.projects}
            showLocation
          />
        }
      >
        <ListWrapper getNextPage={getNextPage}>
          {isNotFound
            ? (
              <ProjectNotFound />
            )
            : (
              <ProjectMilestones
                canCreateMilestones={project.canCreateMilestones}
                milestones={project.milestones?.items ?? []}
                projectId={project.id}
                projectSlug={project.slug}
                projectType={projectType}
                withPadding
                withSectionTitle
              />
            )}
        </ListWrapper>
      </LayoutNoMap>
    </>
  )
}

const PublicParticipationProjectMilestonesPage: React.FC = () => (
  <ProjectMilestonesPage projectType={ProjectType.PublicParticipationProject} />
)

export default PublicParticipationProjectMilestonesPage
